import { z } from 'zod';
import { provenanceSourceSchema, defaultStatusSchema } from './agent.types.js';
/**
 * Realtime wire-format schemas for the realtime-collab-foundation feature.
 *
 * These are the CANONICAL shapes for every future feature that reads from or writes
 * to the collaborative state (in-platform-model-view, external-sharing, multi-editor,
 * accept-underwriting, etc.).
 *
 * Naming convention: wire-format schemas use UPPERCASE snake_case enum values
 * (aligned with Postgres CHECK constraints), while the internal TypeScript
 * `ProvenanceRecord` (in agent.types.ts) uses lowercase. The mapping between them
 * happens inside `recordEdit()` at the persistence boundary — tools and controllers
 * work in TS-native lowercase; the wire format is applied during INSERT.
 * See `context/features/realtime-collab-foundation/design.md` §12 for the mapping table.
 */
// ---------------------------------------------------------------------------
// CellOp — the atomic unit of a state change
// ---------------------------------------------------------------------------
/**
 * Discriminated union on `kind`. Additive by design — future `set_cell` (A1-addressed)
 * or other op kinds can be added without breaking existing consumers.
 *
 * - `set_input`       — single named-range input write (most common)
 * - `set_table_input` — one cell within a tableInputs region (unit-mix, rent roll)
 * - `set_metadata`    — non-cell agent_state write (e.g., generatedExcelUrl)
 */
export const cellOpSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('set_input'),
        namedRange: z.string(),
        value: z.unknown(),
    }),
    z.object({
        kind: z.literal('set_table_input'),
        namedRange: z.string(),
        rowIndex: z.number().int().nonnegative(),
        columnKey: z.string(),
        value: z.unknown(),
    }),
    z.object({
        kind: z.literal('set_metadata'),
        key: z.string(),
        value: z.unknown(),
    }),
]);
// ---------------------------------------------------------------------------
// Provenance — wire-format ProvenanceRecord analog
// ---------------------------------------------------------------------------
/**
 * Wire-format source_type enum. Aligned with the post-migration shipped
 * `provenance_events.source_tag` CHECK constraint:
 *   `USER | SYSTEM_DEFAULT | MARKET_DATA | INFERRED | DERIVED | SCENARIO`
 *
 * Migration `20260421000003_add_scenario_source_tag.sql` added SCENARIO to the
 * shipped CHECK. The `model_edits.source_type` CHECK (in `20260421000000`)
 * uses the same 6 values.
 *
 * Do NOT confuse with `ProvenanceSource` (lowercase) in agent.types.ts —
 * that's the internal TS-native representation. Mapping happens at the
 * `recordEdit()` persistence boundary.
 */
export const wireSourceTypeSchema = z.enum([
    'USER',
    'SYSTEM_DEFAULT',
    'MARKET_DATA',
    'INFERRED',
    'DERIVED',
    'SCENARIO',
]);
export const wireDefaultStatusSchema = z.enum([
    'ACCEPTED',
    'CONFIRMED',
    'OVERRIDDEN',
]);
export const provenanceSchema = z.object({
    source_type: wireSourceTypeSchema,
    source_note: z.string().optional(),
    confidence_flag: z.enum(['high', 'medium', 'low']).optional(),
    semantic_link: z.string().optional(),
    default_offered: z.unknown().optional(),
    default_status: wireDefaultStatusSchema.optional(),
});
// ---------------------------------------------------------------------------
// ActorKind — who/what initiated the edit
// ---------------------------------------------------------------------------
/**
 * Matches `model_edits.actor_kind` CHECK constraint.
 * - `user`   — direct edit via `/model-edit` endpoint (in-platform-model-view)
 * - `agent`  — LangGraph-initiated tool call (set_inputs, generate_excel, etc.)
 * - `system` — system-initiated write (reserved, no active callers at launch)
 * - `import` — bulk/CSV import (reserved for future feature)
 */
export const actorKindSchema = z.enum(['user', 'agent', 'system', 'import']);
// ---------------------------------------------------------------------------
// EditOp — the input to recordEdit() (client- or server-constructed)
// ---------------------------------------------------------------------------
export const editOpSchema = z.object({
    editId: z.string().uuid(),
    conversationId: z.string().uuid(),
    actorKind: actorKindSchema,
    actorUserId: z.string().uuid().nullable(),
    ops: z.array(cellOpSchema),
    provenance: provenanceSchema,
    clientTimestamp: z.string().datetime().optional(),
});
// ---------------------------------------------------------------------------
// ComputedChange + DiffOp — the result of applying an edit
// ---------------------------------------------------------------------------
export const computedChangeSchema = z.object({
    sheet: z.string(),
    address: z.string(),
    namedRange: z.string().optional(),
    prevValue: z.unknown(),
    newValue: z.unknown(),
});
// ---------------------------------------------------------------------------
// builder-ssot-consolidation Phase 4 — DiffOp v2 per-key delta primitive
// ---------------------------------------------------------------------------
/**
 * Per-key inputProvenance delta. The v2 DiffOp envelope (`diffOpV2Schema`)
 * carries `provenanceDeltas: ProvenanceDelta[]` in place of v1's
 * `appliedOps: CellOp[]`. v1 emits cell-level mutations (set_input /
 * set_table_input / set_metadata); v2 emits per-key provenance transitions.
 *
 * Why a separate schema (not just CellOp v2): the inputProvenance store
 * carries richer metadata than the wire-format CellOp — source, defaultStatus,
 * reasoning. DiffOp v2's consumers (web `useConversationChannel`) patch
 * granularly into the unified `views` cache (spec §3), so the delta primitive
 * must carry enough to update both value and metadata fields per touched key.
 *
 * `source` uses TS-native lowercase `provenanceSourceSchema` (mirrors the
 * post-refactor `inputProvenance` storage shape); the v1 wire-format UPPERCASE
 * `wireSourceTypeSchema` is NOT used here — v2 is web-facing only, no DB
 * CHECK constraint alignment needed.
 */
export const provenanceDeltaSchema = z.object({
    key: z.string(), // input name (e.g. "Studio_Units")
    valueBefore: z.unknown(), // prior inputProvenance[key].value
    valueAfter: z.unknown(), // new inputProvenance[key].value
    source: provenanceSourceSchema, // TS-native lowercase (NOT wireSourceTypeSchema)
    defaultStatus: defaultStatusSchema.optional(), // enum-nullable; matches provenanceRecordSchema
    // `record_edit_txn` emits `reasoning` via `->>` which yields JSON `null` when
    // the provenance entry has no reasoning — so the wire shape is `string|null`.
    // Must be `.nullable()` (like defaultStatus) or EVERY no-reasoning v2 broadcast
    // fails safeParse and cross-tab realtime sync silently drops. (Fix 2026-05-21.)
    reasoning: z.string().nullable().optional(),
});
/**
 * Per-entry mutation of a top-level `agent_state` key that lives OUTSIDE
 * `inputProvenance` (e.g. `dealMetadata`, `aerialLatLng`). 2026-05-26 — folds
 * the location-resolver's previously side-channel mutations into the v3
 * `record_edit_txn` atomic transaction so the same `recordEdit()` call commits
 * inputProvenance + dealMetadata + aerialLatLng together AND broadcasts the
 * combined delta on the conversation channel.
 *
 * Shape:
 *   - `path` — top-level agent_state key being mutated
 *   - `key`  — sub-key for `dealMetadata[<inputKey>]`; omitted for paths that
 *              don't have sub-keys (e.g. `aerialLatLng` whole-value writes)
 *   - `prevValue` / `newValue` — opaque JSON; consumers decide how to apply
 *
 * `.optional()` on `metadataChanges` keeps DiffOpV2 back-compat — pre-v3 diffs
 * parse cleanly with `metadataChanges: undefined`; v3 diffs with no metadata
 * mutation also emit `undefined` (the RPC omits the key entirely rather than
 * emitting `[]`).
 */
export const metadataChangeSchema = z.object({
    path: z.enum(['dealMetadata', 'aerialLatLng']),
    key: z.string().optional(),
    prevValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
});
/**
 * DiffOp — the committed, broadcast shape. Produced by `record_edit_txn()`
 * and consumed by subscribers (originating client via REST + all channel
 * subscribers via Supabase Realtime).
 *
 * When `large: true`, the broadcast payload is summary-only to stay under
 * the 3MB Supabase Realtime broadcast cap. Subscribers then fetch the full
 * diff via `GET /api/conversations/:id/diffs?since=<prevRevision>&limit=1`.
 *
 * Cross-field invariant: `large` and `summary` appear together. The refine
 * below enforces this so consumers can trust the presence of `summary`
 * whenever `large: true`.
 */
export const diffOpSummarySchema = z.object({
    cellCount: z.number().int().nonnegative(),
    sheets: z.array(z.string()),
});
export const diffOpSchema = z
    .object({
    editId: z.string().uuid(),
    conversationId: z.string().uuid(),
    revision: z.number().int().positive(),
    prevRevision: z.number().int().nonnegative(),
    actorKind: actorKindSchema,
    actorUserId: z.string().uuid().nullable(),
    // source_type is the wire-format uppercase enum, but the DiffOp field carries
    // it as string so downstream code that doesn't strictly validate the enum still
    // parses DiffOps cleanly (the CHECK constraint at the DB level is the source
    // of truth). Web/API code that DOES care uses wireSourceTypeSchema to refine.
    sourceType: z.string(),
    appliedOps: z.array(cellOpSchema),
    computedChanges: z.array(computedChangeSchema),
    // `{ offset: true }` accepts both `Z` and `±HH:MM` timezone forms.
    // PostgreSQL `timestamptz` rendered through Supabase returns the
    // `+00:00` form (e.g. `2026-04-25T00:46:47.457419+00:00`), which Zod's
    // strict default would reject. Bug repro: realtime-collab-foundation
    // Phase 5 dev walkthrough caught broadcasts being silently dropped at
    // `diffOpSchema.safeParse` because of this mismatch (2026-04-25).
    appliedAt: z.string().datetime({ offset: true }),
    large: z.boolean().optional(),
    summary: diffOpSummarySchema.optional(),
})
    .refine((v) => {
    // If large is true, summary MUST be present
    if (v.large === true && !v.summary)
        return false;
    // If summary is present, large should be true (not strictly required,
    // but the two fields are paired by convention)
    return true;
}, {
    message: 'DiffOp marked `large: true` must include `summary`',
    path: ['summary'],
});
// ---------------------------------------------------------------------------
// builder-ssot-consolidation Phase 4 — DiffOp v2 envelope (provenance-shaped)
// ---------------------------------------------------------------------------
/**
 * DiffOp v2 — provenance-delta-shaped broadcast envelope. Replaces v1's
 * `appliedOps: CellOp[]` with `provenanceDeltas: ProvenanceDelta[]`. v1's
 * `computedChanges` retained as-is (engine-observed cell changes are
 * post-refactor still keyed by sheet+address).
 *
 * Per ADR-0003 (break + force-reload), v1 + v2 do NOT coexist on the wire
 * post-deploy: the X-Arcanum-Protocol header gates which protocol the server
 * publishes per connection. v1 stays in the codebase ONLY for compat reads of
 * any in-flight v1 payload that the client may still have cached before
 * reload. Per ADR-0006, both schemas are co-exported; consumers per-import
 * choose which version they handle.
 */
export const diffOpV2Schema = z
    .object({
    editId: z.string().uuid(),
    conversationId: z.string().uuid(),
    revision: z.number().int().positive(),
    prevRevision: z.number().int().nonnegative(),
    actorKind: actorKindSchema,
    actorUserId: z.string().uuid().nullable(),
    sourceType: z.string(), // permissive — matches v1's tolerance
    provenanceDeltas: z.array(provenanceDeltaSchema), // RENAMED from v1.appliedOps
    computedChanges: z.array(computedChangeSchema), // RETAINED from v1
    // 2026-05-26 — folded from the side-channel admin-client jsonb_set that
    // used to mutate dealMetadata + aerialLatLng outside record_edit_txn.
    // `.optional()` for v2→v3 back-compat: pre-v3 diffs land with undefined.
    metadataChanges: z.array(metadataChangeSchema).optional(),
    appliedAt: z.string().datetime({ offset: true }),
    large: z.boolean().optional(),
    summary: diffOpSummarySchema.optional(),
})
    .refine((v) => v.large !== true || !!v.summary, {
    message: 'DiffOp v2 marked `large: true` must include `summary`',
    path: ['summary'],
});
// ---------------------------------------------------------------------------
// Resync contract — POST /api/conversations/:id/resync
// ---------------------------------------------------------------------------
export const resyncRequestSchema = z.object({
    lastRevision: z.number().int().nonnegative(),
});
/**
 * Resync with a small gap: server returns the missed diffs in revision order.
 * Client applies in order and advances `confirmedRevision` to `toRevision`.
 */
export const resyncDiffsResponseSchema = z.object({
    diffs: z.array(diffOpSchema),
    toRevision: z.number().int().nonnegative(),
});
/**
 * Resync with a large gap (or lastRevision older than retained diffs):
 * server returns a snapshot of agent_state at `fromRevision`. Client
 * replaces its cached state and advances `confirmedRevision` to `fromRevision`.
 * `agentState` is typed as `unknown` here because it's the shipped
 * `conversationAgentStateSchema` shape from agent.types.ts — importing it
 * would create a circular-dep risk; consumers cast/validate on receipt.
 */
export const resyncSnapshotResponseSchema = z.object({
    snapshot: z.object({
        agentState: z.unknown(),
        revision: z.number().int().nonnegative(),
    }),
    fromRevision: z.number().int().nonnegative(),
});
export const resyncResponseSchema = z.union([
    resyncDiffsResponseSchema,
    resyncSnapshotResponseSchema,
]);
// ---------------------------------------------------------------------------
// Diffs pagination — GET /api/conversations/:id/diffs?since=N&limit=M
// ---------------------------------------------------------------------------
/**
 * Wire contract for `GET /api/conversations/:id/diffs`. The API-side schema
 * (`apps/api/src/modules/conversations/conversations.schema.ts`) is the
 * runtime-validated version — it additionally uses `z.coerce.number()` for
 * Express's stringly-typed query values. Keep the `limit` default + max
 * aligned across both sides; `since` has no default (clients must declare it).
 */
export const diffsQuerySchema = z.object({
    since: z.number().int().nonnegative(),
    limit: z.number().int().positive().max(200).default(50),
});
export const diffsResponseSchema = z.object({
    diffs: z.array(diffOpSchema),
    currentRevision: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
// ---------------------------------------------------------------------------
// Realtime publisher tuning knobs (realtime-collab-foundation Phase 3 Session 2)
// ---------------------------------------------------------------------------
/**
 * Tuning knobs for the server-side `RealtimePublisher` singleton (lives in
 * `apps/api/src/shared/realtime-publisher.ts`). Exposed as a shared schema so
 * tests can pass lower thresholds via `configOverrides` without touching the
 * production defaults. The defaults represent the "never come close to the
 * Supabase Realtime 3 MB broadcast cap" setting.
 *
 * Consumers: `RealtimePublisher` constructor defaults; Session 2 unit tests
 * for the payload-size-degrade path.
 */
export const realtimePublisherConfigSchema = z.object({
    /** Drop an idle channel after this many ms. Default: 15 min. */
    idleDropMs: z
        .number()
        .int()
        .positive()
        .default(15 * 60 * 1000),
    /** Degrade to summary if computedChanges length exceeds this. Default: 200. */
    maxComputedChanges: z.number().int().positive().default(200),
    /** Degrade to summary if payload JSON exceeds this byte length. Default: 2 MB. */
    maxPayloadBytes: z
        .number()
        .int()
        .positive()
        .default(2 * 1024 * 1024),
});
